import UIUtils from 'js/lib/UIUtils';
import { cloneDeep } from 'lodash';
import { Field } from '@nexla/@types';

const getDependencies = (fields: Field[], current: Field) => {
  if (!current) return [];
  return fields.filter((field) => {
    if (UIUtils.dependsOnField(field, current.name)) {
      delete field.section;
      return true;
    }
  });
};

const removeDuplicatedFields = (list = []): Field[] => {
  if (!Array.isArray(list)) return list;

  const mapped = list.reduce((previousValue, currentValue) => {
    if (currentValue?.name)
      return {
        ...previousValue,
        [currentValue.name]: currentValue,
      };

    return { ...previousValue };
  }, {});

  return Object.values(mapped);
};

/**
 * @description
 * This function iterates through the provided `fields` and analyzes their dependencies.
 * For fields with a `dependentsLayout` property set to `"2_column"`;
 * The function gathers dependent fields and adds them to the `renderAside` property of the main field.
 * The function then returns a filtered version of the original `fields` array, excluding any fields marked for removal based on their dependency relationships.
 *
 * @HowToUse
 *  - PopUpForm
 */
function fillRenderAside(fields: Field[]) {
  const filledFields = [];
  const indexToRemove = [];
  const toRemoveByName = [];

  const fillDependencies = (checkField: Field) => {
    // Skip renderAside processing for fields using SelectWithDependencies component
    // This allows dependent fields to be rendered in accordions instead of aside
    if (
      checkField.dependentsLayout === '2_column' &&
      checkField.inputType !== 'select-with-dependencies'
    ) {
      fields.forEach((field, index) => {
        if (UIUtils.dependsOnField(field, checkField.name)) {
          // Also skip if the dependent field itself is a select-with-dependencies
          if (field.inputType === 'select-with-dependencies') {
            return;
          }

          // Skip if the dependent field depends on any select-with-dependencies field
          const dependsOnSelectWithDependencies = fields.some(
            (f) =>
              f.inputType === 'select-with-dependencies' && UIUtils.dependsOnField(field, f.name),
          );
          if (dependsOnSelectWithDependencies) {
            return;
          }

          indexToRemove.push(index);
          const toSaveField = cloneDeep(field);
          delete toSaveField.section;

          if (!checkField.renderAside) {
            checkField.renderAside = [toSaveField];
          } else {
            let finalRenderAside = checkField.renderAside;

            finalRenderAside.push(toSaveField);
            getDependencies(fields, toSaveField).forEach((f) => {
              // Also skip nested dependencies that are select-with-dependencies
              if (f.inputType !== 'select-with-dependencies') {
                // Also skip if nested dependency depends on any select-with-dependencies field
                const nestedDependsOnSelectWithDependencies = fields.some(
                  (sf) =>
                    sf.inputType === 'select-with-dependencies' &&
                    UIUtils.dependsOnField(f, sf.name),
                );
                if (!nestedDependsOnSelectWithDependencies) {
                  toRemoveByName.push(f.name);
                  finalRenderAside.push(f);
                }
              }
            });

            checkField.renderAside = removeDuplicatedFields(finalRenderAside);
          }
        }
      });
    }
  };

  fields.forEach((f, index) => {
    filledFields.push(cloneDeep(f));
    const field = filledFields[index];
    fillDependencies(field);
  });

  return filledFields
    .filter((_, i) => !indexToRemove.includes(i))
    .filter((i) => !toRemoveByName.includes(i.name));
}

export const LayoutUtil = {
  fillRenderAside,
  getDependencies,
  removeDuplicatedFields,
};
